///
/// EarthBrowser
///
/// Memory Logging
///
/// Startup memory instrumentation for narrowing down mobile tab kills.
/// JS heap numbers are Chrome-only; on other browsers (mobile Safari) the
/// tagged message sequence itself brackets where memory died.
///
/// Created by Matt Giger on 9/10/2026
/// Copyright (c) 2026 Geodata Labs
///

import { recordCrumb } from "@/earthbrowser/utils/CrashSentinel"

interface PerformanceMemory {
	usedJSHeapSize: number
	jsHeapSizeLimit: number
}

/** Mobile browsers kill tabs at far lower memory thresholds than desktop;
 *  layers use this to choose smaller textures and coarser geometry */
export function isMemoryConstrainedDevice(): boolean {
	const nav = navigator as Navigator & { deviceMemory?: number }
	if (nav.deviceMemory !== undefined && nav.deviceMemory <= 4) return true
	if (/iPhone|iPad|Android/i.test(nav.userAgent)) return true
	// iPadOS reports a Macintosh user agent but supports multitouch
	if (nav.userAgent.includes('Macintosh') && nav.maxTouchPoints > 1) return true
	return false
}

/** Log a tagged memory snapshot (heap numbers where the browser exposes them) */
export function logMemory(tag: string): void {
	const mem = (performance as Performance & { memory?: PerformanceMemory }).memory
	if (mem) {
		const used = Math.round(mem.usedJSHeapSize / 1048576)
		const limit = Math.round(mem.jsHeapSizeLimit / 1048576)
		console.log(`[Mem] ${tag}: ${used}MB / ${limit}MB heap`)
	} else {
		console.log(`[Mem] ${tag}`)
	}
}

/**
 * Periodic heartbeat during the startup window. When a mobile tab is killed
 * for memory there is no error to catch, so the last heartbeat (and the logs
 * between heartbeats) bracket the allocation that killed it.
 */
export function startMemoryHeartbeat(durationMs: number = 90_000, intervalMs: number = 2_000): void {
	const start = Date.now()
	const timer = setInterval(() => {
		const elapsed = Math.round((Date.now() - start) / 1000)
		logMemory(`t+${elapsed}s`)
		// Persisted crumb: after a tab kill the last heartbeat (plus the
		// checkpoints since it) survives in localStorage for the next launch
		recordCrumb("heartbeat")
		if (Date.now() - start >= durationMs) {
			clearInterval(timer)
		}
	}, intervalMs)
}
